import React, { useCallback, useEffect, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import TimePicker from '@amzn/meridian/time-picker';
import Modal from '@amzn/meridian/modal';
import {
  getShiftPlanSettings,
  setShiftPlanSettings,
  getCoralServiceDataFromParent,
  getUserLoginFromParent
} from '../../handler/parentDataHandler';
import { getStationCode } from '../../utils/networkUtil';
import { TEXTS } from '../../utils/constants';
import { publishFlowPlannerCountMetric, FLOW_PLANNER_METRICS as M } from '../../utils/metricUtil';
import {
  validateTimeDistributions,
  getEffectiveIntervals,
  deriveIntervalsFromOpsClock,
  computeOpsClockBounds
} from './timeDistributionModel';

const EMPTY_TIME = '--:--';
const DEFAULT_PERIODS = ['P1', 'P2', 'P3'];
const TIME_FORMAT = { hourCycle: 'h23', hour: '2-digit', minute: '2-digit' };

// TimePicker speaks HH:mm:ss; we store and persist HH:MM. Seconds are always
// zero here, so the conversion is a straight append/trim in each direction.
const toPickerValue = (time) => (time ? `${time}:00` : undefined);
const fromPickerValue = (value) => (value ? value.slice(0, 5) : '');

const TimeDistributions = () => {
  const [intervals, setIntervals] = useState(
    DEFAULT_PERIODS.map((name) => ({ windowName: name, startTime: '', endTime: '' }))
  );
  const [savedIntervals, setSavedIntervals] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [isOpen, setIsOpen] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [opsClock, setOpsClock] = useState(null);

  const fetchOpsClock = useCallback(async () => {
    try {
      const args = {
        processName: 'lmOpsClockService',
        endPoint: 'getOpsClock',
        request: JSON.stringify({ nodeId: getStationCode(), dispatchDate: new Date().toISOString().split('T')[0] })
      };
      const result = await getCoralServiceDataFromParent(args);
      if (result.status === 200 && result.data?.opsClock?.clockEvents) {
        const events = result.data.opsClock.clockEvents;
        const tz = result.data.nodeTimeZone;
        if (!tz) {
          console.error('[TimeDistributions] OpsClock response missing nodeTimeZone');
          return;
        }
        const toHHMM = (epoch) => {
          const d = new Date(epoch * 1000);
          const parts = d
            .toLocaleString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false })
            .split(':');
          const h = parts[0] === '24' ? '00' : parts[0].padStart(2, '0');
          return `${h}:${parts[1]}`;
        };
        setOpsClock({ clockEvents: events, breaks: result.data.opsClock.breaks || [], toHHMM });

        const toMins = (epoch) => {
          const d = new Date(epoch * 1000);
          const h = parseInt(d.toLocaleString('en-US', { timeZone: tz, hour: 'numeric', hour12: false }), 10);
          const m = parseInt(d.toLocaleString('en-US', { timeZone: tz, minute: 'numeric' }), 10);
          return (h === 24 ? 0 : h) * 60 + m;
        };
        setBounds(computeOpsClockBounds(events, toHHMM, toMins));
      } else {
        console.error('[TimeDistributions] getOpsClock returned no usable data', {
          status: result?.status,
          hasOpsClock: Boolean(result?.data?.opsClock),
          stationCode: getStationCode()
        });
      }
    } catch (e) {
      console.error('[TimeDistributions] Failed to fetch OpsClock data', e);
    }
  }, []);
  useEffect(() => {
    fetchOpsClock();
  }, [fetchOpsClock]);

  useEffect(() => {
    if (isOpen && !opsClock) fetchOpsClock();
  }, [isOpen, opsClock, fetchOpsClock]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const request = {
        operationType: 'get',
        queryParams: 'stationCode=' + getStationCode() + '&configType=TIME_DISTRIBUTIONS'
      };
      const response = await getShiftPlanSettings(request);
      if (response.status === 200) {
        const s3Response = await fetch(response.data.url);
        if (s3Response.status === 200) {
          const data = await s3Response.json();
          if (data.intervals && data.intervals.length > 0) {
            setSavedIntervals(data.intervals);
            setIntervals(data.intervals.map((i) => ({ windowName: i.windowName, startTime: '', endTime: '' })));
          }
          if (data.lastUpdatedBy) setLastUpdatedBy(data.lastUpdatedBy);
          if (data.lastUpdatedAt) setLastUpdatedAt(data.lastUpdatedAt);
        }
      }
    } catch (e) {
      console.error('Failed to load time distributions', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  // TimePicker only emits complete, well-formed times, so there's nothing to
  // mask or normalize on the way in.
  const updateInterval = (index, field, value) => {
    setIntervals((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: fromPickerValue(value) };
      return updated;
    });
  };

  const getNextPeriodName = () => {
    const existing = new Set(intervals.map((i) => i.windowName));
    let n = 1;
    while (existing.has('P' + n)) n++;
    return 'P' + n;
  };

  const addPeriod = () => {
    setIntervals((prev) => [...prev, { windowName: getNextPeriodName(), startTime: '', endTime: '' }]);
  };

  const removePeriod = (index) => {
    setIntervals((prev) => prev.filter((_, i) => i !== index));
  };

  const getSavedTime = (windowName, field) => {
    if (!savedIntervals) return EMPTY_TIME;
    const saved = savedIntervals.find((i) => i.windowName === windowName);
    if (!saved || !saved[field]) return EMPTY_TIME;
    return saved[field];
  };

  const hasChanges = () => {
    if (!savedIntervals) {
      return (
        intervals.length !== DEFAULT_PERIODS.length || intervals.some((i) => i.startTime !== '' || i.endTime !== '')
      );
    }
    if (intervals.length !== savedIntervals.length) return true;
    return intervals.some((i) => i.startTime !== '' || i.endTime !== '');
  };

  const canAutoFill = Boolean(opsClock?.clockEvents?.length);

  const handleAutoFill = () => {
    const derived = deriveIntervalsFromOpsClock(opsClock.clockEvents, opsClock.breaks, opsClock.toHHMM);
    if (derived.length === 0) {
      setAlertType('error');
      setAlertMessage('Could not derive time distributions from the ops clock (missing CYCLE_1 data).');
      return;
    }
    // Only interrupt when there is unsaved work to lose. With nothing entered
    // the action is harmless and reversible via Reset, so a prompt on every
    // click is pure friction.
    if (
      hasChanges() &&
      !window.confirm('This will replace your unsaved edits with suggested times from the ops clock. Continue?')
    ) {
      return;
    }
    setIntervals(derived);
    setAlertMessage('');
  };

  const validation = validateTimeDistributions(intervals, savedIntervals, bounds);

  const handleSave = async () => {
    if (!validation.valid) return;
    try {
      // Safe to persist wholesale: validation guarantees every period is
      // complete and continuous, so there are no partial rows to filter out.
      const merged = getEffectiveIntervals(intervals, savedIntervals);

      const request = {
        operationType: 'upload',
        queryParams: 'stationCode=' + getStationCode() + '&configType=TIME_DISTRIBUTIONS'
      };
      const response = await setShiftPlanSettings(request);
      if (response.status === 200) {
        const userLogin = await getUserLoginFromParent()
          .then((r) => r.name || 'unknown')
          .catch(() => 'unknown');
        const now = Math.round(Date.now() / 1000);
        const body = JSON.stringify({
          version: '1.0',
          intervals: merged,
          lastUpdatedBy: userLogin,
          lastUpdatedAt: now
        });
        const putResponse = await fetch(response.data.url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body
        });
        if (!putResponse.ok) {
          throw new Error('S3 upload failed with status ' + putResponse.status);
        }
        publishFlowPlannerCountMetric(M.OVERRIDE_TIME_DISTRIBUTIONS);
        setSavedIntervals(merged);
        setIntervals(merged.map((i) => ({ windowName: i.windowName, startTime: '', endTime: '' })));
        setLastUpdatedBy(userLogin);
        setLastUpdatedAt(now);
        setAlertType('success');
        setAlertMessage('Time Distributions saved successfully! Please Re-run the plan.');
      }
    } catch (e) {
      setAlertType('error');
      setAlertMessage('Failed to save. Error: ' + e);
    }
  };

  const handleReset = () => {
    if (savedIntervals) {
      setIntervals(savedIntervals.map((i) => ({ windowName: i.windowName, startTime: '', endTime: '' })));
    } else {
      setIntervals(DEFAULT_PERIODS.map((name) => ({ windowName: name, startTime: '', endTime: '' })));
    }
    setAlertMessage('');
  };

  if (!isOpen) {
    return (
      <Button type="secondary" size="small" onClick={() => setIsOpen(true)}>
        Time Distributions
      </Button>
    );
  }

  return (
    <>
      <Button type="secondary" size="small" onClick={() => setIsOpen(true)}>
        Time Distributions
      </Button>
      <Modal
        title="Time Distributions"
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          setAlertMessage('');
        }}
        closeLabel="Close"
        width="800px">
        <Column spacing="300">
          <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
            <Row spacing="200">
              <Button type="secondary" size="small" onClick={addPeriod}>
                + Add Period
              </Button>
              <Button type="secondary" size="small" onClick={handleAutoFill} disabled={!canAutoFill}>
                Auto-fill Suggested Periods
              </Button>
              <Button type="secondary" size="small" onClick={handleReset} disabled={!hasChanges()}>
                Reset
              </Button>
              <Button type="primary" size="small" onClick={handleSave} disabled={!hasChanges() || !validation.valid}>
                Save
              </Button>
            </Row>
            <Column alignmentHorizontal="end" spacing="100">
              {bounds ? (
                <Text type="b200" color="secondary">
                  Valid range: {bounds.min} - {bounds.max}
                </Text>
              ) : (
                <Text type="b200" color="secondary">
                  Ops clock unavailable — range checks and suggestions are off
                </Text>
              )}
              {lastUpdatedBy && (
                <Text type="b200" color="secondary">
                  Last updated by {lastUpdatedBy}
                  {lastUpdatedAt ? ' on ' + new Date(lastUpdatedAt * 1000).toLocaleString() : ''}
                </Text>
              )}
            </Column>
          </Row>
          {loading ? (
            <Text>Loading...</Text>
          ) : (
            <Table headerRows={1} showDividers>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Current Start</TableCell>
                <TableCell>Current End</TableCell>
                <TableCell>Override Start</TableCell>
                <TableCell>Override End</TableCell>
                <TableCell width={40} />
              </TableRow>
              {intervals.map((interval, index) => (
                <TableRow key={interval.windowName + index}>
                  <TableCell>
                    <Text type="b300">{interval.windowName}</Text>
                  </TableCell>
                  <TableCell>
                    <Text type="b300">{getSavedTime(interval.windowName, 'startTime')}</Text>
                  </TableCell>
                  <TableCell>
                    <Text type="b300">{getSavedTime(interval.windowName, 'endTime')}</Text>
                  </TableCell>
                  <TableCell>
                    <TimePicker
                      value={toPickerValue(interval.startTime)}
                      onChange={(v) => updateInterval(index, 'startTime', v)}
                      size="small"
                      width={150}
                      format={TIME_FORMAT}
                      showIcon={false}
                      placeholder="HH:MM"
                      invalidInputMessage="Invalid time. The correct time format is hour:minute."
                      error={validation.fieldErrors[`${index}:startTime`] === true}
                    />
                  </TableCell>
                  <TableCell>
                    <TimePicker
                      value={toPickerValue(interval.endTime)}
                      onChange={(v) => updateInterval(index, 'endTime', v)}
                      size="small"
                      width={150}
                      format={TIME_FORMAT}
                      showIcon={false}
                      placeholder="HH:MM"
                      invalidInputMessage="Invalid time. The correct time format is hour:minute."
                      error={validation.fieldErrors[`${index}:endTime`] === true}
                    />
                  </TableCell>
                  <TableCell>
                    <Button type="link" size="small" onClick={() => removePeriod(index)}>
                      <span style={{ fontSize: '18px' }}>x</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          )}
          {/* Surface continuity problems while editing, so the user isn't
              hunting for the offending row after a rejected save. */}
          {!loading && hasChanges() && !validation.valid && (
            <Alert type="warning" size="medium">
              <Column spacing="100">
                {validation.messages.map((message) => (
                  <Text key={message} type="b300">
                    {message}
                  </Text>
                ))}
              </Column>
            </Alert>
          )}
          {alertMessage && (
            <Alert type={alertType} size="medium">
              {alertMessage}
            </Alert>
          )}
        </Column>
      </Modal>
    </>
  );
};

export default TimeDistributions;
